import { inject, Injectable } from '@angular/core';
import { State, Action, StateContext, Store, Selector } from '@ngxs/store';
import { StaffService } from './staff.service';
import {
    IEducationalInfo,
    IEmergencyContact,
    IHiringLeads,
    IStaff,
    IStaffRole,
    StaffAccountCompletion,
    StaffAccountState,
    UpdateStaffInput
} from './staff';
import {
    AccountCompletion,
    BulkDeleteUsers,
    CompleteRegistration,
    CreateFteEducations,
    CreateFteEmergencyContact,
    DeletdFteEmergencyContactOrEducation,
    DeleteUser,
    FinanceUpdateStaff,
    FteAccountCompletion,
    GenerateTestUsers,
    GetCasualStaffs,
    GetHiringLeads,
    GetSingleFteEmergencyContactOrEducation,
    GetSingleStaff,
    GetStaff,
    GetStaffByFilter,
    GetUserRole,
    GetUserWithCode,
    HiringLead,
    InviteCasualStaffs,
    SubmitRegistration,
    UpdateFteEducations,
    UpdateFteEmergencyContact,
    UpdateStaff,
    UpdateTimeOff,
    UserDepartment
} from './staff.actions';
import { catchError, tap } from 'rxjs';
import { LoadingState } from '@tsin-core/models';
import { DepartmentStore } from '../departments/department.store';

export interface GenerateTestUsersResult {
    created: number;
    defaultPassword: string;
    cognito: { success: number; failed: number };
    auth: { success: number; failed: number };
    mfa: { success: number; failed: number };
}

export interface StaffStateModel {
    staff: IStaff[] | null;
    selectedStaff: IStaff | null;
    userWithCode: IStaff | null;
    loading: LoadingState;
    hiringLeads: IHiringLeads[];
    accountCompletion: StaffAccountState | null;
    educations: IEducationalInfo[];
    emergencyContacts: IEmergencyContact[];
    emergencyContact: IEmergencyContact | null;
    education: IEducationalInfo | null;
    loadingSingleStaff: boolean;
    loadingStaff: boolean;
    staffRole: IStaffRole | null;
    error: string | null;
    lastGenerationResult: GenerateTestUsersResult | null;
}

const defaults = {
    staff: [],
    hiringLeads: [],
    selectedStaff: null,
    userWithCode: null,
    staffRole: null,
    accountCompletion: null,
    educations: [],
    emergencyContacts: [],
    emergencyContact: null,
    education: null,
    loading: LoadingState.notLoading,
    loadingStaff: false,
    loadingSingleStaff: false,
    error: null,
    lastGenerationResult: null
};

@State<StaffStateModel>({
    name: 'staffState',
    defaults
})
@Injectable()
export class StaffState {
    staffService: StaffService = inject(StaffService);
    private store: Store = inject(Store);

    @Selector()
    static staff(state: StaffStateModel) {
        return state.staff;
    }
    @Selector()
    static loading(state: StaffStateModel) {
        return state.loadingStaff;
    }
    @Selector()
    static hiringLeads(state: StaffStateModel) {
        return state.hiringLeads;
    }

    @Selector()
    static selectedStaff(state: StaffStateModel) {
        return state.selectedStaff;
    }
    @Selector()
    static userWithCode(state: StaffStateModel) {
        return state.userWithCode;
    }

    @Selector()
    static getLoading(state: StaffStateModel) {
        return state.loading;
    }

    @Selector()
    static getError(state: StaffStateModel) {
        return state.error;
    }
    @Selector()
    static getStaffRole(state: StaffStateModel) {
        return state.staffRole;
    }

    @Selector()
    static getUserAccountState(state: StaffStateModel) {
        return state.accountCompletion;
    }

    @Selector()
    static getEductaions(state: StaffStateModel) {
        return state.educations;
    }
    @Selector()
    static getEductaion(state: StaffStateModel) {
        return state.education;
    }
    @Selector()
    static getEmergencyContacts(state: StaffStateModel) {
        return state.emergencyContacts;
    }
    @Selector()
    static getEmergencyContact(state: StaffStateModel) {
        return state.emergencyContact;
    }
    @Action(GetCasualStaffs)
    getCasualStaffs(ctx: StateContext<StaffStateModel>, action: GetCasualStaffs) {
        const state = ctx.getState();
        ctx.patchState({ loadingStaff: true });
        return this.staffService.getCasualStaffs(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    staff: res,
                    loadingStaff: false
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loadingStaff: false
                });
                throw err;
            })
        );
    }

    @Action(InviteCasualStaffs)
    inviteCasualStaffs(ctx: StateContext<StaffStateModel>, action: InviteCasualStaffs) {
        const state = ctx.getState();
        // ctx.patchState({ loadingStaff: true });
        return this.staffService.inviteCasualStaffs(action.input).pipe(
            tap((res) => {
                ctx.patchState({});
            }),
            catchError((err) => {
                ctx.patchState({});
                throw err;
            })
        );
    }

    @Action(AccountCompletion)
    completeAccount(ctx: StateContext<StaffStateModel>, action: AccountCompletion) {
        const state = ctx.getState();
        const payload = action.payload;

        if (!payload) {
            ctx.patchState({ accountCompletion: null });
            return;
        }

        const prev: Partial<StaffAccountState> = state.accountCompletion ?? {};

        const updatedAccount: StaffAccountState = {
            id: payload.id || prev.id || null,
            personalInformation: {
                ...prev.personalInformation,
                ...payload.personalInformation,
                finished: payload?.personalInformation?.dateOfBirth
                    ? true
                    : prev.personalInformation?.finished ?? false
            },
            contactInformation: {
                ...prev.contactInformation,
                ...payload.contactInformation,
                finished: !!payload?.contactInformation?.phone
                    ? true
                    : prev.contactInformation?.finished ?? false
            },
            scCharacteristics: {
                ...prev.scCharacteristics,
                ...payload.scCharacteristics,
                finished: !!payload?.scCharacteristics?.bodyType
                    ? true
                    : prev.scCharacteristics?.finished ?? false
            },
            scRoles: {
                ...prev.scRoles,
                ...payload.scRoles,
                finished: !!payload?.scRoles?.rolesAdditionalInfo?.length
                    ? true
                    : prev.scRoles?.finished ?? false
            },
            education: {
                education: payload.education || prev.education?.education,
                finished: payload.education ? true : prev.education?.finished
            },
            emergencyContacts: {
                emergencyContact:
                    payload.emergencyContacts || prev.emergencyContacts?.emergencyContact,
                finished: payload.emergencyContacts ? true : prev.emergencyContacts?.finished
            }
        };

        ctx.patchState({ accountCompletion: updatedAccount });

        console.log('==== Updated AccountCompletion ====', updatedAccount);
    }

    @Action(SubmitRegistration)
    submitReg(ctx: StateContext<StaffStateModel>, action: SubmitRegistration) {
        return this.staffService.submitReg(action.payload).pipe(
            tap((res) => {}),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(CompleteRegistration)
    completeReg(ctx: StateContext<StaffStateModel>, action: CompleteRegistration) {
        const state = ctx.getState();

        return this.staffService.completeAccount(action.payload).pipe(
            tap((res) => {
                ctx.patchState({
                    selectedStaff: res as IStaff,
                    userWithCode: res as IStaff
                });
                this.store.dispatch(
                    new AccountCompletion(action.payload as StaffAccountCompletion)
                );
            }),
            catchError((err) => {
                ctx.patchState({
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(FteAccountCompletion)
    fteAccountCompletion(ctx: StateContext<StaffStateModel>, action: FteAccountCompletion) {
        const state = ctx.getState();
        return this.staffService.fteAccountCompletion(action.payload).pipe(
            tap((res) => {
                ctx.patchState({
                    selectedStaff: res as IStaff,
                    userWithCode: res as IStaff
                });
                this.store.dispatch(
                    new AccountCompletion(action.payload as StaffAccountCompletion)
                );
            }),
            catchError((err) => {
                ctx.patchState({
                    error: err.message
                });
                throw err;
            })
        );
    }
    @Action(GetSingleFteEmergencyContactOrEducation)
    getReferences(
        ctx: StateContext<StaffStateModel>,
        action: GetSingleFteEmergencyContactOrEducation
    ) {
        const state = ctx.getState();

        return this.staffService.getFteEmergencyContactOrEmergency(action.payload).pipe(
            tap((res) => {
                ctx.patchState({
                    educations: res as IEducationalInfo[],
                    emergencyContacts: res as IEmergencyContact[]
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(DeletdFteEmergencyContactOrEducation)
    deleteFteEmergencyContactOrEmergency(
        ctx: StateContext<StaffStateModel>,
        action: DeletdFteEmergencyContactOrEducation
    ) {
        const state = ctx.getState();
        return this.staffService.deleteFteEmergencyContactOrEmergency(action.id).pipe(
            tap((res) => {
                ctx.patchState({
                    educations: state.educations.filter((edu) => edu.id !== action.id),
                    emergencyContacts: state.emergencyContacts.filter((edu) => edu.id !== action.id)
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(CreateFteEducations)
    createFteEducations(ctx: StateContext<StaffStateModel>, action: CreateFteEducations) {
        const state = ctx.getState();
        return this.staffService.createFteEducations(action.payload).pipe(
            tap((res) => {
                ctx.patchState({
                    educations: [res, ...state.educations],
                    education: res
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(CreateFteEmergencyContact)
    createFteEmergencyContact(
        ctx: StateContext<StaffStateModel>,
        action: CreateFteEmergencyContact
    ) {
        const state = ctx.getState();
        return this.staffService.createFteEmergencyContact(action.payload).pipe(
            tap((res) => {
                ctx.patchState({
                    emergencyContacts: [res, ...state.emergencyContacts],
                    emergencyContact: res
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(UpdateFteEducations)
    updateFteEducations(ctx: StateContext<StaffStateModel>, action: UpdateFteEducations) {
        const state = ctx.getState();
        return this.staffService.updateFteEducations(action.payload).pipe(
            tap((res) => {
                ctx.patchState({
                    educations: state.educations.map((edu) =>
                        edu.id === action.payload.id ? { ...edu, res } : edu
                    ),
                    education: res
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(UpdateFteEmergencyContact)
    updateFteEmergencyContact(
        ctx: StateContext<StaffStateModel>,
        action: UpdateFteEmergencyContact
    ) {
        const state = ctx.getState();
        return this.staffService.updateFteEmergencyContact(action.payload).pipe(
            tap((res) => {
                ctx.patchState({
                    emergencyContacts: state.emergencyContacts.map((contact) =>
                        contact.id === action.payload.id ? { ...contact, res } : contact
                    )
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(UpdateTimeOff)
    updateTime(ctx: StateContext<StaffStateModel>, action: UpdateTimeOff) {
        const state = ctx.getState();

        return this.staffService.updateTimeOff(action.input).pipe(
            tap((res: any) => {
                ctx.patchState({
                    selectedStaff: {
                        ...state.selectedStaff,
                        userTimeOff: res.updatedUser.userTimeOff
                    } as IStaff
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(GetUserRole)
    setUserRole(ctx: StateContext<StaffStateModel>, action: GetUserRole) {
        const state = ctx.getState();

        const updatedUserRole: IStaffRole = {
            role: action.payload.role,
            staffType: action.payload.staffType
        };
        ctx.patchState({ staffRole: updatedUserRole });
    }

    @Action(GetStaff)
    staff(ctx: StateContext<StaffStateModel>, action: GetStaff) {
        const state = ctx.getState();
        ctx.patchState({ loading: LoadingState.loadingList });
        return this.staffService.staff().pipe(
            tap((staff: IStaff[]) => {
                ctx.patchState({
                    staff,
                    loading: LoadingState.notLoading,
                    error: null
                });
            }),
            catchError((error) => {
                ctx.patchState({
                    loading: LoadingState.notLoading,
                    error: error.message
                });
                throw error;
            })
        );
    }

    @Action(GetSingleStaff)
    singleStaff(ctx: StateContext<StaffStateModel>, action: GetSingleStaff) {
        const state = ctx.getState();
        ctx.patchState({ loading: LoadingState.loadingList });
        return this.staffService.singleStaff(action.payload).pipe(
            tap((singleStaff: IStaff) => {
                ctx.patchState({
                    selectedStaff: singleStaff,
                    loading: LoadingState.notLoading,
                    error: null
                });
            }),
            catchError((error) => {
                ctx.patchState({
                    loading: LoadingState.notLoading,
                    error: error.message
                });
                throw error;
            })
        );
    }

    @Action(GetUserWithCode)
    userWithCode(ctx: StateContext<StaffStateModel>, action: GetUserWithCode) {
        const state = ctx.getState();
        return this.staffService.userByCode(action.payload).pipe(
            tap((single: IStaff) => {
                ctx.patchState({
                    selectedStaff: single,
                    userWithCode: single,
                    error: null
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(GetStaffByFilter)
    filterStaff(ctx: StateContext<StaffStateModel>, action: GetStaffByFilter) {
        const state = ctx.getState();
        ctx.patchState({ loading: LoadingState.loadingList });
        return this.staffService.staffByFilter(action.payload).pipe(
            tap((staff: IStaff[]) => {
                ctx.patchState({
                    staff,
                    loading: LoadingState.notLoading,
                    error: null
                });
            }),
            catchError((error) => {
                ctx.patchState({
                    loading: LoadingState.notLoading,
                    error: error.message
                });
                throw error;
            })
        );
    }

    @Action(UpdateStaff)
    updateStaff(ctx: StateContext<StaffStateModel>, action: UpdateStaff) {
        const state = ctx.getState();
        ctx.patchState({ loading: LoadingState.loadingAddUpdate });
        return this.staffService.updateStaff(action.payload).pipe(
            tap((res: IStaff) => {
                ctx.patchState({
                    staff: state.staff!.map((staff) =>
                        staff.id === action.payload.id ? { ...staff, res } : staff
                    ),
                    selectedStaff: res as IStaff,
                    loading: LoadingState.loadingAddUpdate,
                    error: null
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    loading: LoadingState.notLoading,
                    error: err.message
                });
                throw err;
            })
        );
    }
    @Action(FinanceUpdateStaff)
    financeUpdateStaff(ctx: StateContext<StaffStateModel>, action: FinanceUpdateStaff) {
        const state = ctx.getState();
        return this.staffService.financeUpdateStaff(action.payload).pipe(
            tap((res: IStaff) => {
                ctx.patchState({
                    staff: state.staff!.map((staff) =>
                        staff.id === action.payload.id ? { ...staff, res } : staff
                    ),
                    selectedStaff: res,

                    error: null
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    error: err.message
                });
                throw err;
            })
        );
    }

    @Action(GetHiringLeads)
    get(ctx: StateContext<StaffStateModel>, action: GetHiringLeads) {
        const state = ctx.getState();
        return this.staffService.getHiringLeads(action.input).pipe(
            tap((res) => {
                ctx.patchState({
                    hiringLeads: res
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(HiringLead)
    hiringLead(ctx: StateContext<StaffStateModel>, action: HiringLead) {
        const state = ctx.getState();
        return this.staffService.hiringLead(action.payload).pipe(
            tap((res) => {
                ctx.patchState({
                    staff: state.staff?.map((s) =>
                        s.id === action.payload.userId ? { ...s, res } : s
                    ),
                    selectedStaff: res as IStaff
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    error: err.error.message
                });
                console.log('=====>>>E', err.error.message);
                throw err;
            })
        );
    }

    @Action(UserDepartment)
    userDepartment(ctx: StateContext<StaffStateModel>, action: UserDepartment) {
        const state = ctx.getState();
        const departments = this.store.selectSnapshot(DepartmentStore.getDepartments);
        const department = departments.find((d) => d.id === action.payload.departmentId);
        return this.staffService.userDepartment(action.payload).pipe(
            tap((res) => {
                ctx.patchState({
                    staff: state.staff?.map((s) =>
                        s.id === action.payload.userId
                            ? { ...s, department: department?.name as string }
                            : s
                    ),
                    selectedStaff: {
                        ...state.selectedStaff,
                        department: department?.name as string
                    } as IStaff
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(DeleteUser)
    deleteUser(ctx: StateContext<StaffStateModel>, action: DeleteUser) {
        const state = ctx.getState();
        return this.staffService.deleteUser(action.id).pipe(
            tap(() => {
                ctx.patchState({
                    staff: state.staff?.filter((s) => s.id !== action.id) ?? []
                });
            }),
            catchError((err) => {
                ctx.patchState({ error: err.message });
                throw err;
            })
        );
    }

    @Action(BulkDeleteUsers)
    bulkDeleteUsers(ctx: StateContext<StaffStateModel>, action: BulkDeleteUsers) {
        const state = ctx.getState();
        return this.staffService.bulkDeleteUsers(action.ids).pipe(
            tap(() => {
                const idSet = new Set(action.ids);
                ctx.patchState({
                    staff: state.staff?.filter((s) => !idSet.has(s.id)) ?? []
                });
            }),
            catchError((err) => {
                ctx.patchState({ error: err.message });
                throw err;
            })
        );
    }

    @Action(GenerateTestUsers)
    generateTestUsers(ctx: StateContext<StaffStateModel>, action: GenerateTestUsers) {
        return this.staffService.generateTestUsers(action.payload).pipe(
            tap((res) => {
                ctx.patchState({ lastGenerationResult: res });
                // Re-fetch the staff list so the newly created users appear
                this.store.dispatch(new GetStaffByFilter({ department: 'ALL', staffType: 'All' }));
            }),
            catchError((err) => {
                ctx.patchState({ error: err.message });
                throw err;
            })
        );
    }

    @Selector()
    static getLastGenerationResult(state: StaffStateModel): GenerateTestUsersResult | null {
        return state.lastGenerationResult;
    }
}
